import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { formatCount } from "@memorang/helpers";
import type { WritingSection } from "@memorang/types/src/session/new-session";
import CustomBottomSheetTextInput from "components/CustomBottomSheetTextInput";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Text, TextInput } from "react-native-paper";

const MAX_LENGTH = 6000;
const MIN_HEIGHT = 150;
const MAX_HEIGHT = 400;

type Props = {
	value?: string;
	onChangeText: (value: string) => void;
	sections?: WritingSection[];
	disabled?: boolean;
};

const WritingInput = ({
	placeholder,
	onChangeText,
	value,
	wordCount,
	label,
	disabled,
}: {
	placeholder: string;
	onChangeText: (value: string) => void;
	value: string | undefined;
	wordCount: number;
	label?: string;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const containerRef = useRef<View>(null);
	const [inputHeight, setInputHeight] = useState(MIN_HEIGHT);

	// biome-ignore lint/correctness/useExhaustiveDependencies: value triggers re-measurement of textarea height
	useEffect(() => {
		if (!(isWeb && containerRef.current)) {
			return;
		}
		const el = containerRef.current as unknown as HTMLElement;
		const textarea = el.querySelector("textarea");
		if (!textarea) {
			return;
		}

		textarea.style.height = `${MIN_HEIGHT}px`;
		const newHeight = Math.min(
			Math.max(textarea.scrollHeight, MIN_HEIGHT),
			MAX_HEIGHT,
		);
		textarea.style.height = `${newHeight}px`;
		setInputHeight(newHeight);
	}, [value]);
	return (
		<View ref={containerRef} style={{ gap: 4 }}>
			<TextInput
				placeholder={placeholder}
				disabled={disabled}
				onChangeText={onChangeText}
				contentStyle={{
					minWidth: 300,
					height: inputHeight,
					maxHeight: MAX_HEIGHT,
					overflowY: "auto",
				}}
				placeholderTextColor={theme.colors.onSurfaceDisabled}
				multiline
				mode="outlined"
				value={value}
				autoComplete="off"
				autoCorrect={false}
				spellCheck={false}
				autoCapitalize="none"
				maxLength={MAX_LENGTH}
			/>
			<Text variant="bodySmall">
				{t("writing.wordCount", {
					count: wordCount,
					word: formatCount(t("common.word"), wordCount),
				})}
			</Text>
		</View>
	);
};

const Writing = ({ value, onChangeText, sections, disabled }: Props) => {
	const { t } = useTranslation();

	const wordCount = value?.trim().split(/\s+/).filter(Boolean).length ?? 0;
	const placeholder = t("writing.placeholder");

	const [sectionsValue, setSectionsValue] = useState<Record<string, string>>(
		{},
	);

	const handleChangeText = (section: string, text: string) => {
		if (!sections) {
			onChangeText(text);
			return;
		}

		const newSectionsValue = { ...sectionsValue, [section]: text };
		setSectionsValue(newSectionsValue);

		let newText = "";

		for (const section of sections) {
			if (newSectionsValue[section.label]) {
				newText += `${section.label}: ${newSectionsValue[section.label]}\n\n`;
			}
		}

		onChangeText(newText);
	};

	if (isWeb) {
		if (!sections) {
			return (
				<WritingInput
					placeholder={placeholder}
					onChangeText={onChangeText}
					value={value}
					wordCount={wordCount}
					disabled={disabled}
				/>
			);
		}
		return sections.map((section) => {
			const value = sectionsValue[section.label];

			const wordCount = value?.trim().split(/\s+/).filter(Boolean).length ?? 0;

			return (
				<WritingInput
					key={section.label}
					placeholder={section.placeholder}
					onChangeText={(text) => handleChangeText(section.label, text)}
					value={value}
					wordCount={wordCount}
					label={section.label}
					disabled={disabled}
				/>
			);
		});
	}

	return (
		<CustomBottomSheetTextInput
			value={value}
			onChangeText={onChangeText}
			placeholder={placeholder}
			wordCount={wordCount}
			maxLength={MAX_LENGTH}
			disabled={disabled}
		/>
	);
};

export default Writing;
